import { isNonEmptyArray } from "@p-ban/common/src/utils/ArrayUtils"
import { parseYearMonthDay, parseYearMonthDayHourMinute } from "@p-ban/common/src/utils/DateUtils"
import DecimalMath from "@p-ban/common/src/utils/DecimalMath"
import { parseNumber } from "@p-ban/common/src/utils/NumberUtils"
import { removeBrTag } from "@p-ban/common/src/utils/StringUtils"

import type {
  EstimateSuccessResult,
  SummaryTrialPurchaseManufactResult,
  SummaryTrialPurchaseMountResult,
  SummaryTrialPurchaseTotalResult,
} from "../../../api/estimate/csComEst.d"
import ESTIMATE_CONSTANT from "../constant/estimateConstant"
import type { ManufactureCourse } from "../constant/manufactureCourse"
import type {
  ManufactureResult,
  MassProductionSimulationResult,
  MountResult,
  ParsedResult,
  ParsedResultBase,
  TotalResult,
} from "../types/parsedResult"

const getProcurementResult = (result: EstimateSuccessResult): ParsedResult["procurement"] => {
  const { partsProcurementCourse } = result.summaryTrialPurchaseParam

  const isProcurement = partsProcurementCourse === ESTIMATE_CONSTANT.部品調達.あり.value
  if (!isProcurement) {
    return
  }

  return {
    isIndividual: true,
    amount: null,
    days: null,
  }
}

const parseResult = (result: EstimateSuccessResult): ParsedResult => {
  const design = getDesignResult(result)
  const manufacture = getManufactureResult(result)
  const mount = getMountResult(result)
  const procurement = getProcurementResult(result)
  const discounts = getDiscounts(result)
  const total = getTotalResults(result)
  const massProductionSimulation = getMassProductionSimulationResult(result)

  return {
    design,
    manufacture,
    mount,
    procurement,
    discounts,
    total,
    paramStringify: JSON.stringify(result.summaryTrialPurchaseParam),
    resultStringify: JSON.stringify(result.summaryTrialPurchaseResult),
    massProductionSimulation,
  }
}

export default parseResult

const getDesignResult = (result: EstimateSuccessResult): ParsedResult["design"] => {
  const designResult = result.summaryTrialPurchaseResult.summaryTrialPurchaseDesignResult
  const errors = result.summaryTrialPurchaseResult.dsnErrMsgs

  if (designResult) {
    if (designResult.emptyResult) {
      const _errors = errors.map(removeBrTag)
      return {
        isIndividual: true,
        days: null,
        amount: null,
        errors: isNonEmptyArray(_errors) ? _errors : null,
      }
    }
    return {
      isIndividual: false,
      days: Number.parseInt(designResult.day, 10),
      amount: designResult.minorTotal,
      errors: null,
    }
  }
  return undefined
}

const getManufactureResult = (result: EstimateSuccessResult): ParsedResult["manufacture"] => {
  const count = parseNumber(result.cnt, null)

  if (count === null) {
    throw new Error("数値への変換に失敗しました。")
  }

  const manufactureResults = result.summaryTrialPurchaseResult.summaryTrialPurchaseManufactResults

  const func = <T extends ManufactureCourse["value"]>(
    result: SummaryTrialPurchaseManufactResult<T>,
    errors: string[]
  ): ManufactureResult => {
    if (result.emptyResult) {
      return {
        isIndividual: true,
        days: null,
        amount: null,
        errors: isNonEmptyArray(errors) ? errors : null,
        volumeDiscountAmount: null,
        unitPrice: null,
        initialCost: null,
        factory: result.factory,
      }
    }
    return {
      isIndividual: false,
      days: Number.parseInt(result.period, 10),
      amount: result.minorTotal,
      volumeDiscountAmount: result.volumeDiscountAmount
        ? Number.parseInt(result.volumeDiscountAmount.replace(/(￥|,)/g, ""), 10)
        : null,
      errors: null,
      unitPrice: DecimalMath.trunc(result.minorTotal / count, 2),
      initialCost: Number.parseInt(result.initialCost.replace(/(￥|,)/g, ""), 10),
      factory: result.factory,
    }
  }

  const r = result.summaryTrialPurchaseResult
  const commonErrors = r.mftErrMsgs.map(removeBrTag)
  const 量産以外のErrors = r.mftTrialErrMsgs?.map(removeBrTag) ?? []
  const massProductionErrors = r.mftTrialInitCostErrMsgs?.map(removeBrTag) ?? []
  const ultraQuickCourseErrors = r.mftKbnCheck?.fctUqvalidators?.map(removeBrTag) ?? []
  const superQuickCourseErrors = r.mftKbnCheck?.fctSqvalidators?.map(removeBrTag) ?? []
  const quickCourseErrors = r.mftKbnCheck?.fctQvalidators?.map(removeBrTag) ?? []
  const normalCourseErrors = r.mftKbnCheck?.fctNvalidators?.map(removeBrTag) ?? []

  const errors = {
    ultraQuick: [...commonErrors, ...量産以外のErrors, ...ultraQuickCourseErrors],
    superQuick: [...commonErrors, ...量産以外のErrors, ...superQuickCourseErrors],
    quick: [...commonErrors, ...量産以外のErrors, ...quickCourseErrors],
    normal: [...commonErrors, ...量産以外のErrors, ...normalCourseErrors],
    massProduction: [...commonErrors, ...massProductionErrors],
  }

  return manufactureResults.length === 5
    ? {
        ultraQuick: func(manufactureResults[0], errors.ultraQuick),
        superQuick: func(manufactureResults[1], errors.superQuick),
        quick: func(manufactureResults[2], errors.quick),
        normal: func(manufactureResults[3], errors.normal),
        massProduction: func(manufactureResults[4], errors.massProduction),
      }
    : undefined
}

const getMountResult = (result: EstimateSuccessResult): ParsedResult["mount"] => {
  const manufactureResults = getManufactureResult(result)
  const procurementResult = getProcurementResult(result)
  const mountResult = result.summaryTrialPurchaseResult.summaryTrialPurchaseMountResult
  const totalResults = result.summaryTrialPurchaseResult.summaryTrialPurchaseTotalResults
  const errors = result.summaryTrialPurchaseResult.furnErrMsgs

  if (!mountResult) {
    return
  }

  const func = (totalResult: SummaryTrialPurchaseTotalResult, manufactureResult?: ParsedResultBase): MountResult => {
    if (mountResult.emptyResult) {
      const _errors = errors.map(removeBrTag)
      return {
        isIndividual: true,
        days: null,
        amount: null,
        errors: isNonEmptyArray(_errors) ? _errors : null,
        factoryId: mountResult.factoryId,
        mountWay: mountResult.mountWay,
      }
    }
    // 部品調達の金額と日数は実装に含まれてるため、結果から引く
    // TODOこの処理で正しいか確認
    return {
      isIndividual: false,
      days:
        getMountDays(mountResult, totalResult.mntStartDayDiffFlg, manufactureResult?.days) -
        (procurementResult?.days ?? 0),
      amount: mountResult.minorTotal - (procurementResult?.amount ?? 0),
      errors: null,
      factoryId: mountResult.factoryId,
      mountWay: mountResult.mountWay,
    }
  }

  if (totalResults.length === 1) {
    const r = func(totalResults[0], manufactureResults?.ultraQuick)
    return {
      ultraQuick: r,
      superQuick: r,
      quick: r,
      normal: r,
      massProduction: r,
    }
  }

  return {
    ultraQuick: func(totalResults[0], manufactureResults?.ultraQuick),
    superQuick: func(totalResults[1], manufactureResults?.superQuick),
    quick: func(totalResults[2], manufactureResults?.quick),
    normal: func(totalResults[3], manufactureResults?.normal),
    massProduction: func(totalResults[4], manufactureResults?.massProduction),
  }
}

const getMountDays = (
  mountResult: Exclude<SummaryTrialPurchaseMountResult, { emptyResult: true }>,
  mntStartDayDiffFlg: boolean,
  基板製造日数: number | null = null
) => {
  const { metalmaskPeriod: メタルマスク製造日数, mountBasePeriod: ライン調整日数, mountPeriod: 実装日数 } = mountResult
  const 部品調達日数 = mountResult.partsProcurementPeriod ?? 0
  let メタルマスクが終わるまでの日数 = ライン調整日数 + メタルマスク製造日数

  // 製造結果がない場合
  if (基板製造日数 === null) {
    return Math.max(部品調達日数, メタルマスクが終わるまでの日数) + 実装日数
  }

  const 基板配送日数 = 1
  const 製造から配送までの日数 = 基板製造日数 + 基板配送日数

  /**
   * Q,SQ,UQコースで締切以降～18時までの場合に、基板製造とメタルマスク製造の開始日がずれる。
   * 基板製造が1日遅れる（メタルマスク製造が1日早まる）ためその差分を吸収する
   * TODO 部品調達も一緒の場合はどうなる？
   */
  if (mntStartDayDiffFlg) {
    メタルマスクが終わるまでの日数 -= 1
  }

  const 実装準備完了日数 = Math.max(部品調達日数, メタルマスクが終わるまでの日数)

  /**
   * 製造が終わり実装センターに届いてから、実装準備完了までの差分の日数を追加する
   * 実装準備が先に終わってる場合は追加しない
   */
  let diff = 実装準備完了日数 - 製造から配送までの日数
  if (diff < 0) {
    diff = 0
  }

  return 基板配送日数 + diff + 実装日数
}

const getDiscounts = (result: EstimateSuccessResult): ParsedResult["discounts"] => {
  const manufactureResults = getManufactureResult(result)

  if (!manufactureResults) {
    return
  }

  const func = (result: ManufactureResult) => {
    const discounts = []

    if (result.volumeDiscountAmount !== null) {
      const discount = {
        name: "ボリュームディスカウント",
        amount: result.volumeDiscountAmount,
      }
      discounts.push(discount)
    }

    return discounts.length === 0 ? undefined : discounts
  }

  return {
    ultraQuick: func(manufactureResults.ultraQuick),
    superQuick: func(manufactureResults.superQuick),
    quick: func(manufactureResults.quick),
    normal: func(manufactureResults.normal),
    massProduction: func(manufactureResults.massProduction),
  }
}
const getTotalResults = (result: EstimateSuccessResult): NonNullable<ParsedResult["total"]> => {
  const totalResults = result.summaryTrialPurchaseResult.summaryTrialPurchaseTotalResults
  const design = getDesignResult(result)
  const manufacture = getManufactureResult(result)
  const mount = getMountResult(result)
  const procurement = getProcurementResult(result)

  const func = (
    totalResult: SummaryTrialPurchaseTotalResult,
    designResult: ParsedResultBase | undefined,
    manufactureResult: ParsedResultBase | undefined,
    mountResult: ParsedResultBase | undefined,
    procurementResult: ParsedResult["procurement"]
  ): TotalResult => {
    if (
      totalResult.emptyResult ||
      designResult?.isIndividual ||
      manufactureResult?.isIndividual ||
      mountResult?.isIndividual ||
      procurementResult?.isIndividual
    ) {
      const designErrors = designResult?.errors ?? []
      const manufactureErrors = manufactureResult?.errors ?? []
      const mountErrors = mountResult?.errors ?? []

      // エラーメッセージにbrタグが含まれてしまってるので取り除きます。
      // TODO サーバー側のエラーメッセージ定義を直してこの処理を外す。
      const errors = [...designErrors, ...manufactureErrors, ...mountErrors]

      return {
        isIndividual: true,
        days: null,
        amount: null,
        amountWithoutTax: null,
        arrivalDate: null,
        orderLimit: null,
        errors: isNonEmptyArray(errors) ? errors : null,
      }
    }

    const designDays = designResult?.days ?? 0
    const manufactureDays = manufactureResult?.days ?? 0
    const mountDays = mountResult?.days ?? 0
    const procurementDays = procurementResult?.days ?? 0
    let totalDays = designDays + manufactureDays + mountDays + procurementDays
    // 設計と製造を同時に注文している場合は製造手配で+1日する
    if (designResult && manufactureResult) {
      totalDays += 1
    }

    return {
      isIndividual: false,
      days: totalDays,
      amount: totalResult.totalInt,
      amountWithoutTax: totalResult.minorTotalInt,
      arrivalDate: parseYearMonthDay(totalResult.dateOfArrival),
      orderLimit: totalResult.orderLimit ? parseYearMonthDayHourMinute(totalResult.orderLimit) : null,
      errors: null,
    }
  }

  if (totalResults.length === 1) {
    const r = func(totalResults[0], design, manufacture?.ultraQuick, mount?.ultraQuick, procurement)
    return {
      ultraQuick: r,
      superQuick: r,
      quick: r,
      normal: r,
      massProduction: r,
    }
  }

  return {
    ultraQuick: func(totalResults[0], design, manufacture?.ultraQuick, mount?.ultraQuick, procurement),
    superQuick: func(totalResults[1], design, manufacture?.superQuick, mount?.superQuick, procurement),
    quick: func(totalResults[2], design, manufacture?.quick, mount?.quick, procurement),
    normal: func(totalResults[3], design, manufacture?.normal, mount?.normal, procurement),
    massProduction: func(totalResults[4], design, manufacture?.massProduction, mount?.massProduction, procurement),
  }
}

const getMassProductionSimulationResult = (result: EstimateSuccessResult): MassProductionSimulationResult | null => {
  const { mftSimuResult } = result.summaryTrialPurchaseResult
  const manufacture = getManufactureResult(result)

  if (!mftSimuResult) {
    return null
  }

  if (!manufacture || manufacture.normal.isIndividual || manufacture.massProduction.isIndividual) {
    return null
  }

  const repeatOrderCount = mftSimuResult.simuCount ?? 1

  const repeatManufactureCount = mftSimuResult.nextManuCnt

  const normalFirstPrice = manufacture.normal.amount
  const normalRepeatPrice = mftSimuResult.normalNextRepAmount
  const massProductionFirstPrice = manufacture.massProduction.amount
  const massProductionRepeatPrice = mftSimuResult.initNextRepAmount

  return {
    type: mftSimuResult.sumuResultType,
    repeatManufactureCount,
    repeatOrderCount,
    normal: {
      firstPrice: normalFirstPrice,
      initialCost: 0,
      manufactureCost: normalFirstPrice,
      repeatPrice: normalRepeatPrice,
      unitPrice: DecimalMath.trunc(normalRepeatPrice / repeatManufactureCount, 2),
      // 総額が0円の場合は、リピート1回目の価格をリピートの総額として扱います。
      grandTotalPrice: (mftSimuResult.normalTotalRepAmount || normalRepeatPrice) + normalFirstPrice,
    },
    massProduction: {
      firstPrice: massProductionFirstPrice,
      initialCost: manufacture.massProduction.initialCost,
      manufactureCost: massProductionFirstPrice - manufacture.massProduction.initialCost,
      repeatPrice: massProductionRepeatPrice,
      unitPrice: DecimalMath.trunc(massProductionRepeatPrice / repeatManufactureCount, 2),
      // 総額が0円の場合は、リピート1回目の価格をリピートの総額として扱います。
      grandTotalPrice: (mftSimuResult.initTotalRepAmount || massProductionRepeatPrice) + massProductionFirstPrice,
    },
  }
}
